// ────────────────────────────────────────────────────────────
// DEAL DESK — DATA SOURCE (tract-first)
// ────────────────────────────────────────────────────────────
// One interface, two implementations:
//
//   fixtureSource — serves tract 34-7N-63W + the White deal from
//                   /fixtures/white-deal.json while we iterate.
//   apiSource     — the eventual live path: tract + deal endpoints
//                   returning the same DealRows shape from D1. The
//                   underlying tables stay deal-scoped until the
//                   pipeline writes tract-scoped rows; this layer is
//                   where that difference is absorbed. Render code
//                   never changes.

import type { DealRows } from './rows';
import type { TractBundle, TractSummary, DealRecord, DealLens, DealListRow } from '../types';
import { buildTractBundle, deriveDealRecords, deriveOwnerFractions, dealLens } from './derive';

export interface TractView {
  tract: TractBundle;
  deals: DealRecord[];
}

export interface DealView {
  deal: DealRecord;
  tract: TractBundle;
  lens: DealLens;
}

export interface DealDeskSource {
  listTracts(): Promise<TractSummary[]>;
  /** Every deal the title agent has written (stage 'titled'), newest first. */
  listTitledDeals(): Promise<DealListRow[]>;
  getTract(trs: string): Promise<TractView>;
  getDeal(dealId: string): Promise<DealView>;
}

// ─── mojibake repair (fixture only) ─────────────────────────
// The fixture was captured through a Windows console that misread the
// UTF-8 bytes D1 actually holds (cp437 rendering). Live D1 rows arrive
// clean, so this repair belongs to the fixture loader alone.
const MOJIBAKE: Array<[RegExp, string]> = [
  [/ΓåÆ/g, '→'],
  [/ΓÇö/g, '—'],
  [/ΓÇô/g, '–'],
  [/ΓÇ£/g, '“'],
  [/ΓÇ¥/g, '”'],
  [/ΓÇÿ/g, '‘'],
  [/ΓÇÖ/g, '’'],
  [/├ù/g, '×'],
  [/┬╜/g, '½'],
  [/┬╝/g, '¼'],
  [/┬╖/g, '·'],
];

function fixString(s: string): string {
  let out = s;
  for (const [re, rep] of MOJIBAKE) out = out.replace(re, rep);
  return out;
}

function fixDeep<T>(value: T): T {
  if (typeof value === 'string') return fixString(value) as unknown as T;
  if (Array.isArray(value)) return value.map(fixDeep) as unknown as T;
  if (value && typeof value === 'object') {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(value as Record<string, unknown>)) out[k] = fixDeep(v);
    return out as unknown as T;
  }
  return value;
}

/** PS 5.1 ConvertTo-Json can collapse single-element arrays; coerce back. */
function asArray<T>(v: T[] | T | null | undefined): T[] {
  if (v == null) return [];
  return Array.isArray(v) ? v : [v];
}

/** Exported for the chain-math demo script, which loads the dump from disk. */
export function normalizeRows(raw: any): DealRows {
  return {
    deal: asArray(raw.deal),
    working_theories: asArray(raw.working_theories),
    math_worksheets: asArray(raw.math_worksheets),
    chain_links: asArray(raw.chain_links),
    flags: asArray(raw.flags),
    findings: asArray(raw.findings),
    files: asArray(raw.files),
    revision_log: asArray(raw.revision_log),
  };
}

export { fixDeep as repairDumpEncoding };

// ─── fixture implementation ─────────────────────────────────

let cachedRows: Promise<DealRows> | null = null;

function loadFixtureRows(): Promise<DealRows> {
  if (!cachedRows) {
    cachedRows = (async () => {
      const r = await fetch('/fixtures/white-deal.json');
      if (!r.ok) throw new Error(`Fixture load failed: HTTP ${r.status}`);
      return normalizeRows(fixDeep(await r.json()));
    })();
    // A failed load should retry on the next call, not stick forever.
    cachedRows.catch(() => { cachedRows = null; });
  }
  return cachedRows;
}

function summarize(tract: TractBundle, dealCount: number): TractSummary {
  return {
    trs: tract.trs,
    county: tract.county,
    chainLength: tract.chain.length,
    confidence: tract.confidence,
    untracedTotal: tract.untracedTotal,
    grossAcres: tract.grossAcres,
    dealCount,
  };
}

const fixtureSource: DealDeskSource = {
  async listTracts(): Promise<TractSummary[]> {
    const rows = await loadFixtureRows();
    const tract = buildTractBundle(rows);
    return [summarize(tract, rows.deal.length)];
  },

  async listTitledDeals(): Promise<DealListRow[]> {
    const rows = await loadFixtureRows();
    const tract = buildTractBundle(rows);
    return deriveDealRecords(rows)
      .filter(d => d.stage === 'titled')
      .map(d => ({
        ...d,
        nmaEstimate: rows.deal.find(x => x.id === d.id)?.nma_estimate ?? null,
        confidence: tract.confidence,
      }))
      .sort((a, b) => b.createdAt.localeCompare(a.createdAt));
  },

  async getTract(trs: string): Promise<TractView> {
    const rows = await loadFixtureRows();
    const tract = buildTractBundle(rows);
    if (tract.trs !== trs) {
      throw new Error(`Fixture holds tract ${tract.trs}, not ${trs}. Deal Desk is in fixture mode.`);
    }
    return { tract, deals: deriveDealRecords(rows) };
  },

  async getDeal(dealId: string): Promise<DealView> {
    const rows = await loadFixtureRows();
    const deals = deriveDealRecords(rows);
    const deal = deals.find(d => d.id === dealId);
    if (!deal) {
      throw new Error(`Fixture holds ${deals.map(d => d.id).join(', ') || 'no deals'}, not ${dealId}. Deal Desk is in fixture mode.`);
    }
    const tract = buildTractBundle(rows);
    const lens = dealLens(tract, deal.subjectParty, rows.deal[0]?.nma_estimate ?? null);
    return { deal, tract, lens };
  },
};

// ─── live implementation (D1) ───────────────────────────────
// Reads real rows through /api/deal-desk/rows (same DealRows shape as
// the fixture) and runs the identical derive layer. Live rows arrive
// as clean UTF-8, so NO mojibake repair — that belongs to the fixture
// path alone.

import { authHeaders } from '../../lib/authHeaders';

async function api<T>(path: string): Promise<T> {
  const r = await fetch(path, { credentials: 'include', headers: authHeaders() });
  if (!r.ok) {
    let msg = await r.text();
    try { msg = JSON.parse(msg).error ?? msg; } catch { /* raw text */ }
    throw new Error(`API ${r.status}: ${msg}`);
  }
  return r.json() as Promise<T>;
}

function fetchRows(query: string): Promise<DealRows> {
  return api<any>(`/api/deal-desk/rows?${query}`).then(normalizeRows);
}

const apiSource: DealDeskSource = {
  async listTracts(): Promise<TractSummary[]> {
    const { deals } = await api<{ deals: DealListRow[] }>('/api/deal-desk/deals');
    const seen = new Set<string>();
    const trsList = deals.map(d => d.trs).filter(t => t && !seen.has(t) && seen.add(t));
    const summaries = await Promise.all(trsList.map(async trs => {
      const rows = await fetchRows(`trs=${encodeURIComponent(trs)}`);
      return summarize(buildTractBundle(rows), rows.deal.length);
    }));
    return summaries;
  },

  async listTitledDeals(): Promise<DealListRow[]> {
    const { deals } = await api<{ deals: DealListRow[] }>('/api/deal-desk/deals');
    return deals
      .map(d => ({ ...d, nmaEstimate: d.nmaEstimate ?? null, confidence: d.confidence ?? null }))
      .sort((a, b) => b.createdAt.localeCompare(a.createdAt));
  },

  async getTract(trs: string): Promise<TractView> {
    const rows = await fetchRows(`trs=${encodeURIComponent(trs)}`);
    return { tract: buildTractBundle(rows), deals: deriveDealRecords(rows) };
  },

  async getDeal(dealId: string): Promise<DealView> {
    const rows = await fetchRows(`dealId=${encodeURIComponent(dealId)}`);
    const deals = deriveDealRecords(rows);
    const deal = deals.find(d => d.id === dealId);
    if (!deal) throw new Error(`Deal ${dealId} not returned in its tract rows`);
    const tract = buildTractBundle(rows);
    const nmaStored = rows.deal.find(d => d.id === dealId)?.nma_estimate ?? null;
    const lens = dealLens(tract, deal.subjectParty, nmaStored);
    return { deal, tract, lens };
  },
};

export function getDealSource(): DealDeskSource {
  return apiSource;
}

export { deriveOwnerFractions };
